import '~/customizeLocationPanel/editLocationModal/editLocationModal.container.autoload';
import '~/map/page/matchup/matchupData.container.autoload';
import {
  useCallback, useMemo,
} from 'react';
import {
  matchUpDataAllDataSections, matchupDataOnlyInfoPanelSection,
} from '~/_shared/components/matchUpDataModal/matchUpDataModal.component';
import { MarkerSettingType } from '~/_shared/types/markers/visualSettings.enums';
import { useTranslation } from '~/_shared/utils/hooks';
import { openUrlInNewTab } from '~/_shared/utils/link/link';
import { CustomizationLevel } from '~/customizeMarkerPopup/customizeLabelAndMarker.types';
import { useModal } from '~/modal/useModal.hook';
import { RightSidebarOpeneableApps } from '~/sidebar/sidebarApps/rightSidebar/rightSidebarConfiguration';
import type { SpreadsheetRowData } from '~/spreadsheet/useSpreadsheetRowData';
import { useActiveMarkerIdSelector } from '~/store/frontendState/activeMapElements/activeMapElements.selectors';
import { checkIsSalesforceMap } from '~/store/mapInfo/mapInfo.helpers';
import {
  useIsConnectedLayeredMapSelector, useMapInfoDataSelector,
} from '~/store/mapInfo/mapInfo.selectors';
import { useMapMarkerWaypointDataSelector } from '~/store/mapSettings/directions/mapSettingsDirections.selectors';
import { type EditLocationCallbackProps } from './contextMenuStructure.types';
import { createRightSidebarContextMenuCallbackProps } from './editLocationCallbacks.helpers';

type EditLocationCallbacksProps = Readonly<{
  areBubbleItemsIncluded: boolean;
  rowData: SpreadsheetRowData | null;

  selectOpenableApp: (app: RightSidebarOpeneableApps, isOpened: boolean) => void;
}>;

export const useEditLocationCallbacks = (props: EditLocationCallbacksProps): EditLocationCallbackProps => {
  const [t] = useTranslation();
  const activeMarkerId = useActiveMarkerIdSelector();
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();
  const mapInfo = useMapInfoDataSelector();
  const waypointInfo = useMapMarkerWaypointDataSelector(activeMarkerId);
  const { openModal: openCustomizeLabelAndMarker } = useModal('LabelAndMarkerSettings');
  const { openModal: openEditMarker } = useModal('EditLocationData');
  const { openModal: openEditImage } = useModal('ImageLibrary');
  const { openModal: openMatchLocationModal } = useModal('MatchupData');

  const { selectOpenableApp } = props;

  const disableDataProps = useMemo(() => {
    let isDisabled = false;
    let disabledTooltip: string | undefined = undefined;
    let textOverride: string | undefined = undefined;

    if (checkIsSalesforceMap(mapInfo)) {
      if (props.rowData?.editLink) {
        textOverride = t('salesforce.spreadsheetData.edit.inSalesforce');
      }
      else {
        isDisabled = true;
        disabledTooltip = t('salesforce.spreadsheetData.cannotEditInApp');
      }
    }
    else if (isLayeredMapConnected) {
      isDisabled = true;
    }

    return {
      isDisabled,
      disabledTooltip,
      textOverride,
    };
  }, [isLayeredMapConnected, mapInfo, props.rowData?.editLink, t]);

  const disableMarkerProps = useMemo(() => {
    let isDisabled = false;
    let disabledTooltip: string | undefined = undefined;

    if (waypointInfo) {
      isDisabled = true;
      disabledTooltip = t('Route waypoints cannot be customized.');
    }

    return {
      isDisabled,
      disabledTooltip,
    };
  }, [t, waypointInfo]);

  const editDataCallback = useCallback(() => {
    if (!activeMarkerId) {
      return;
    }

    if (checkIsSalesforceMap(mapInfo)) {
      if (props.rowData?.editLink) {
        openUrlInNewTab(props.rowData.editLink);
      }
      return;
    }

    openEditMarker({
      spreadsheetRowId: activeMarkerId,
    });
  }, [activeMarkerId, mapInfo, openEditMarker, props.rowData?.editLink]);

  const editMarkerCallback = useCallback(() => {
    if (!activeMarkerId) {
      throw new Error('useEditLocationCallbacks: Missing marker Id');
    }
    openCustomizeLabelAndMarker({
      customizationLevel: CustomizationLevel.Individual,
      overrideGroupMarkers: true,
      spreadsheetRowId: activeMarkerId,
      markerSettingsTypes: [MarkerSettingType.Marker],
    });
  }, [activeMarkerId, openCustomizeLabelAndMarker]);

  const editPanelCallback = useCallback(() => {
    selectOpenableApp(RightSidebarOpeneableApps.EditPanel, true);
  }, [selectOpenableApp]);

  const editImageCallback = useCallback(() => {
    if (activeMarkerId) {
      openEditImage({ markerId: activeMarkerId });
    }
  }, [openEditImage, activeMarkerId]);

  const categorizeDataCallback = useCallback(() => {
    openMatchLocationModal({
      ensureLocationDataIsSelected: false,
      sections: isLayeredMapConnected ? matchupDataOnlyInfoPanelSection : matchUpDataAllDataSections,
    });
  }, [openMatchLocationModal, isLayeredMapConnected]);

  return useMemo(() => ({
    categorizeData: categorizeDataCallback,
    editData: createRightSidebarContextMenuCallbackProps({
      callback: editDataCallback,
      ...disableDataProps,
    }),
    editImages: createRightSidebarContextMenuCallbackProps({ callback: editImageCallback }),
    editMarker: createRightSidebarContextMenuCallbackProps({
      callback: editMarkerCallback,
      ...disableMarkerProps,
    }),
    editPanel: createRightSidebarContextMenuCallbackProps({
      isDisabled: !props.areBubbleItemsIncluded,
      callback: editPanelCallback,
    }),
  }), [categorizeDataCallback, editDataCallback, editImageCallback, editMarkerCallback,
    disableMarkerProps, disableDataProps, props.areBubbleItemsIncluded, editPanelCallback]);
};
